import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import { useMaskito } from '@maskito/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import LinearProgress from '@mui/material/LinearProgress'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { useTranslation } from 'react-i18next'
import { useLoginMutation } from './auth-api'
import { CHANGE_PASSWORD_PATH, setCredentials } from './authSlice'
import logo from '../../assets/logo.png'
import parkingGreenBg from '../../assets/images/bg-parking-green.jpg'
import { phoneMaskSpaced } from '@/utils/phone-masks'

const pageSx = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  px: 0,
  py: 0,
  '--ink': '#163451',
  '--muted': '#5b738a',
  '--line': '#cfdeea',
  '--brand-a': '#0f9e5f',
  '--brand-b': '#20c179',
  '--brand-c': '#79d65c',
  background:
    'radial-gradient(circle at 12% 86%, rgba(24,128,215,0.16) 0%, transparent 44%), radial-gradient(circle at 90% 14%, rgba(255,167,62,0.14) 0%, transparent 34%), linear-gradient(154deg, #e7eef7 0%, #f2f7fd 39%, #e8eef6 100%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background:
      'linear-gradient(90deg, rgba(18,52,82,0.05) 1px, transparent 1px), linear-gradient(180deg, rgba(18,52,82,0.05) 1px, transparent 1px)',
    backgroundSize: '56px 56px',
    opacity: 0.28,
    animation: 'gridDrift 34s linear infinite'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: '-22%',
    pointerEvents: 'none',
    background:
      'radial-gradient(circle at 20% 78%, rgba(24,128,215,0.22) 0%, transparent 48%), radial-gradient(circle at 86% 16%, rgba(255,167,62,0.2) 0%, transparent 52%)',
    animation: 'auraShift 24s ease-in-out infinite alternate'
  },
  '@keyframes shellIn': {
    '0%': { opacity: 0, transform: 'translateY(22px) scale(0.988)' },
    '100%': { opacity: 1, transform: 'translateY(0) scale(1)' }
  },
  '@keyframes auraShift': {
    '0%': { transform: 'translate3d(-2%, -1%, 0) scale(1)' },
    '100%': { transform: 'translate3d(3%, 2%, 0) scale(1.05)' }
  },
  '@keyframes gridDrift': {
    '0%': { transform: 'translate3d(0, 0, 0)' },
    '100%': { transform: 'translate3d(56px, 56px, 0)' }
  },
  '@keyframes heroFloat': {
    '0%': { transform: 'scale(1) translate3d(0, 0, 0)' },
    '100%': { transform: 'scale(1.1) translate3d(1.6%, -1%, 0)' }
  },
  '@keyframes shineSweep': {
    '0%': { transform: 'translate3d(-60%, 0, 0) rotate(16deg)', opacity: 0.18 },
    '50%': { opacity: 0.32 },
    '100%': { transform: 'translate3d(150%, 0, 0) rotate(16deg)', opacity: 0.15 }
  },
  '@keyframes chipLift': {
    '0%': { transform: 'translate3d(0, 0, 0)' },
    '100%': { transform: 'translate3d(0, -8px, 0)' }
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&, & *': {
      animation: 'none !important',
      transition: 'none !important'
    }
  }
}

const shellSx = {
  position: 'relative',
  zIndex: 1,
  width: '100%',
  maxWidth: '100vw',
  minHeight: '100vh',
  borderRadius: 0,
  overflow: 'hidden',
  backgroundColor: 'rgba(255,255,255,0.88)',
  border: 'none',
  boxShadow: 'none',
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', lg: '1.48fr 1fr' },
  animation: 'shellIn 500ms ease-out both'
}

const fieldSx = {
  mt: 2,
  '& .MuiInputLabel-root': {
    // color: 'var(--muted)',
    fontWeight: 600,
    fontSize: 15,
    lineHeight: 2,
    ml: 1,
  },
  '& .MuiOutlinedInput-root': {
    minHeight: 66,
    backgroundColor: '#fff',
    borderRadius: '999px',
    p: 1.5,
    '&.Mui-focused': {
      backgroundColor: '#fff',
      boxShadow: 'none',
    },
    '& fieldset': {
      borderColor: 'var(--line)'
    },
    '&:hover fieldset': {
      borderColor: '#a3b8cb'
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--brand-a)',
    }
  },
  '& .MuiInputBase-input': {
    color: 'var(--ink)',
    fontWeight: 600,
    fontSize: 17,
    px: 1.5,
    py: 1,
  },
}

const Login = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const [login, { isLoading, isError }] = useLoginMutation()
  const [hidePassword, setHidePassword] = useState(true)
  const [remember, setRemember] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [openSmsDialog, setOpenSmsDialog] = useState(false)
  const [progress, setProgress] = useState(false)
  const [form, setForm] = useState({ login: '', password: '' })
  const phoneInputRef = useMaskito({ options: phoneMaskSpaced })

  const fromPath = location?.state?.from?.pathname
  const from = fromPath && fromPath !== '/login' ? fromPath : '/'
  const canSubmit = Boolean(form.login.trim()) && Boolean(form.password)
  const welcomeTitle = t('welcome_to_AiParking')

  const handleChange = ({ target }) => {
    setForm((prev) => ({
      ...prev,
      [target.name]: target.value
    }))
  }

  const handleSubmit = async (event) => {
    event?.preventDefault()
    if (!canSubmit) return

    try {
      const user = await login({
        username: form.login.toLowerCase(),
        password: form.password
      }).unwrap()

      const mustChangePassword = Boolean(user.must_change_password)

      dispatch(
        setCredentials({
          user: form.login.toLowerCase(),
          token: user.access_token,
          refreshToken: user.refresh_token,
          mustChangePassword
        })
      )

      navigate(mustChangePassword ? CHANGE_PASSWORD_PATH : from, {
        replace: true
      })
    } catch (error) {
      // error is rendered by isError from RTK query
    }
  }

  const goSms = () => {
    setProgress(true)

    setTimeout(() => {
      setOpenDialog(false)
      setProgress(false)
      setOpenSmsDialog(true)
    }, 2000)
  }

  return (
    <Box component="main" sx={pageSx}>
      <Box sx={shellSx}>
        <Box
          sx={{
            position: 'relative',
            p: { xs: 3.5, sm: 5.4, md: 7, lg: 8 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'hidden',
            minHeight: { xs: 380, sm: 520, lg: '100%' },
            background:
              'linear-gradient(138deg, rgba(10,46,82,0.98) 0%, rgba(17,78,136,0.92) 58%, rgba(28,135,198,0.88) 100%)'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: '-10%',
              background: `url(${parkingGreenBg}) center/cover no-repeat`,
              opacity: 0.66,
              transformOrigin: 'center',
              animation: 'heroFloat 40s ease-in-out infinite alternate',
              willChange: 'transform'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: '-24% auto -24% -60%',
              width: { xs: 260, sm: 420, md: 520, lg: 620 },
              background:
                'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.17) 48%, rgba(255,255,255,0.04) 100%)',
              animation: 'shineSweep 14s ease-in-out infinite',
              willChange: 'transform'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(5,24,42,0.2) 0%, rgba(8,33,60,0.68) 100%), radial-gradient(circle at 74% 12%, rgba(255,255,255,0.26) 0%, transparent 40%)'
            }}
          />

          <Box sx={{ position: 'relative', zIndex: 1, color: '#f5f9fd' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.8,
                px: 1.9,
                py: 0.7,
                borderRadius: 99,
                border: '1px solid rgba(255,255,255,0.4)',
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                backgroundColor: 'rgba(255,255,255,0.12)'
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  backgroundColor: '#8ce0ff',
                  boxShadow: '0 0 0 4px rgba(140,224,255,0.28)'
                }}
              />
              <Typography>Ai Parking Platform</Typography>
            </Box>

            <Typography
              sx={{
                mt: 3.8,
                maxWidth: 940,
                fontSize: { xs: 44, md: 72, xl: 88 },
                lineHeight: 0.94,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                textWrap: 'balance',
                fontFamily: '"Avenir Next", "Sora", "Trebuchet MS", sans-serif'
              }}
            >
              {welcomeTitle}
            </Typography>

            <Typography
              sx={{
                mt: 2.6,
                maxWidth: 760,
                fontSize: { xs: 17, md: 24 },
                lineHeight: 1.58,
                opacity: 0.98
              }}
            >
              Контролируйте парковку, проверяйте задолженности и проводите оплату
              без лишних переключений между экранами.
            </Typography>
          </Box>

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              mt: 5.5,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(160px, 1fr))' },
              gap: 1.8,
              maxWidth: 940
            }}
          >
            {[
              { title: 'Проверка долга', value: '< 1 сек' },
              { title: 'Работа системы', value: '24/7' },
              { title: 'Платежи', value: 'Realtime' }
            ].map((item, index) => (
              <Box
                key={item.title}
                sx={{
                  borderRadius: 3.2,
                  px: 2.2,
                  py: 1.8,
                  color: '#f6fbff',
                  border: '1px solid rgba(255,255,255,0.38)',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  animation: 'chipLift 8.2s ease-in-out infinite alternate',
                  animationDelay: `${index * 0.7}s`
                }}
              >
                <Typography sx={{ fontSize: 13, opacity: 0.92 }}>{item.title}</Typography>
                <Typography sx={{ mt: 0.55, fontSize: 25, fontWeight: 800, lineHeight: 1.1 }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            p: { xs: 3.5, sm: 5.4, md: 7.8, lg: 9.2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            background:
              'linear-gradient(174deg, rgba(255,255,255,0.84) 0%, rgba(242,248,255,0.97) 100%)'
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 660,
              p: { xs: 3.6, sm: 4.8, md: 5.4 },
              borderRadius: 5.6,
              border: '1px solid rgba(200,219,235,0.96)',
              boxShadow: '0 30px 58px rgba(22, 52, 82, 0.16)',
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(245,251,255,0.97) 100%)'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.6, mb: 2.1 }}>
              <Box component="img" src={logo} alt="AiParking" sx={{ width: 92, height: 92 }} />
            </Box>

            <Typography
              component="h1"
              sx={{
                textAlign: 'center',
                color: 'var(--ink)',
                fontWeight: 800,
                fontSize: { xs: 42, md: 58 },
                letterSpacing: '-0.02em',
                fontFamily: '"Avenir Next", "Sora", "Trebuchet MS", sans-serif',
                cursor: 'default',
              }}
            >
              {t('login_form_title')}
            </Typography>

            <Typography
              sx={{
                mt: 1.2,
                mb: 3.5,
                textAlign: 'center',
                color: 'var(--muted)',
                fontWeight: 600,
                fontSize: 18,
                cursor: 'default',
              }}
            >
              {t('login_form_description')}
            </Typography>

            {isError && (
              <Alert severity="error" variant="outlined" sx={{ mb: 2.5, borderRadius: 3 }}>
                <AlertTitle>{t('authorization_error')}</AlertTitle>
                {t('incorrect_login_or_password.')}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                id="login"
                label={t('username')}
                name="login"
                autoComplete="username"
                value={form.login}
                onChange={handleChange}
                fullWidth
                required
                autoFocus
                sx={fieldSx}
                slotProps={{
                  inputLabel: {
                    required: false,
                  }
                }}
              />
              <TextField
                margin="normal"
                name="password"
                label={t('password')}
                type={hidePassword ? 'password' : 'text'}
                autoComplete="current-password"
                value={form.password}
                onChange={handleChange}
                fullWidth
                required
                sx={fieldSx}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setHidePassword((prev) => !prev)}
                          
                          aria-label="toggle password visibility"
                          sx={{
                            borderRadius: '50%',
                          }}
                        >
                          {hidePassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  },
                  inputLabel: {
                    required: false,
                  }
                }}
              />

              <Button
                type="submit"
                fullWidth
                loading={isLoading}
                disabled={!canSubmit}
                sx={(theme) => ({
                  mt: 2.5,
                  borderRadius: 3.4,
                  border: 'none',
                  py: 1.5,
                  color: '#FAFAFA',
                  fontWeight: 800,
                  fontSize: 20,
                  textTransform: 'none',
                  background:
                    'linear-gradient(90deg, var(--brand-a) 0%, var(--brand-b) 70%, var(--brand-c) 100%)',
                  // boxShadow: '0 18px 34px rgba(15,158,95,0.33)',
                  '&:hover': {
                    opacity: 0.9,
                  //   boxShadow: '0 22px 40px rgba(15,158,95,0.4)'
                  }
                })}
              >
                {t('login_form_button')}
              </Button>

              <Box
                sx={{
                  mt: 1.8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 0.9
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={remember}
                      onChange={() => setRemember((prev) => !prev)}
                      sx={{ color: 'var(--brand-a)' }}
                    />
                  }
                  label={t('remember_me')}
                  sx={{ color: 'var(--muted)' }}
                />

                <Button
                  variant="text"
                  onClick={() => setOpenDialog(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: 'var(--brand-a)',
                    fontSize: 16
                  }}
                >
                  {t('forgot_your_password?')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        {progress && <LinearProgress />}
        <DialogTitle>{t('forgot_your_password?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('to_recover_your_password_you_must_enter_your_phone_number')}
          </DialogContentText>
          <TextField
            inputRef={phoneInputRef}
            autoFocus
            margin="dense"
            label={t('phone_number')}
            type="tel"
            fullWidth
            variant="standard"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('close')}</Button>
          <Button onClick={goSms}>{t('next')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openSmsDialog} onClose={() => setOpenSmsDialog(false)}>
        <DialogTitle>{t('enter_the_code')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('an_sms_with_a_code_has_been_sent_to_your_phone_number.')}
          </DialogContentText>
          <TextField
            autoFocus
            margin="normal"
            label={t('code')}
            type="text"
            size="small"
            variant="standard"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSmsDialog(false)}>{t('close')}</Button>
          <Button onClick={() => setOpenSmsDialog(false)}>{t('send_code')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Login
